import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
  useMemo,
} from "react";
import { useToasts } from "../../LayoutComponents/ToastContext";
import i18n from "../../Services/i18n";
import ServiceOccurenceService from "../../Services/serviceOccurenceService";
import UserServiceService from "../../Services/userServiceService";
import ServiceOccurenceForm from "../QuickFormComponents/ServiceOccurenceForm/ServiceOccurenceForm";
import LoadingSpinner from "../../LayoutComponents/LoadingSpinner";
import styles from "./UpsertServiceOccurenceModal.module.css";
import FileService from "../../Services/fileService";

const TAB_KEYS = {
  INFO: "info",
  NOTES: "notes",
  MEDIAS: "medias",
};

const UpsertServiceOccurenceModal = forwardRef(function UpsertServiceOccurenceModal(
  { serviceOccurenceExternalId, client, horse, onSave, modalControls },
  ref,
) {
  const { addToast } = useToasts();
  const formRef = useRef(null);
  const didInitRef = useRef(false);

  const [serviceOcc, setServiceOcc] = useState({});
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCurrentTabDirty, setIsCurrentTabDirty] = useState(false);
  const [isNewSO, setIsNewSO] = useState(!serviceOccurenceExternalId);
  const [activeTab, setActiveTab] = useState(TAB_KEYS.INFO);

  const setSaveVisible = (visible) => {
    modalControls?.setHideSaveButton?.(!visible);
  };

  const setSaveAndCloseVisible = (visible) => {
    modalControls?.setHideSaveAndCloseButton?.(!visible);
  };

  const canShowSaveActions = useMemo(() => {
    return activeTab === TAB_KEYS.INFO;
  }, [activeTab]);

  const applyModalUiPolicy = () => {
    setSaveVisible(canShowSaveActions);
    setSaveAndCloseVisible(canShowSaveActions);

    const canGenerate = !isCurrentTabDirty && !isNewSO && !!serviceOcc?.externalId;

    modalControls?.setGenerateReportDisabled?.(!canGenerate);
    modalControls?.setGenerateReportLabel?.(i18n.t("Modal.GenerateReport.ServiceReport"));
    modalControls?.setTitle?.(
      serviceOcc?.externalId
        ? i18n.t("BoardServiceClient.ServiceOccurenceUpdate")
        : i18n.t("BoardServiceClient.ServiceOccurenceCreate"),
    );
  };

  useImperativeHandle(ref, () => ({
    isDirty() {
      return !!formRef.current?.isDirty?.() || isCurrentTabDirty;
    },
    async validate() {
      return await formRef.current?.validate?.();
    },
    async serviceOccurenceExternalId() {
      return serviceOcc.externalId;
    },
    async save() {
      try {
        const data = formRef.current.getFormData();

        if (data.formData.defaultImageIsFromDefaultSource) {
          const result = await FileService.UploadFromLibrary(data.formData.defaultImageId);
          data.formData.defaultImageId = result.id;
        }

        let finalId;
        let savedSo = null;

        if (data.serviceOccurence?.externalId) {
          data.formData.externalId = data.serviceOccurence.externalId;
          const result = await ServiceOccurenceService.Update(data.formData);
          if (!result) return false;

          finalId = data.serviceOccurence.externalId;
          savedSo = await formRef.current.saved(finalId);
        } else {
          finalId = await ServiceOccurenceService.Create(data.formData);
          if (!finalId) return false;

          setIsNewSO(false);
          savedSo = await formRef.current.saved(finalId);
        }

        if (savedSo) {
          setServiceOcc(savedSo);
        }

        setIsCurrentTabDirty(false);
        onSave?.();
        return true;
      } catch (ex) {
        console.error(ex);
        addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
        return false;
      }
    },
  }));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [serviceOccData] = await Promise.all([
          serviceOccurenceExternalId
            ? ServiceOccurenceService.GetDetailServiceOccurence(serviceOccurenceExternalId)
            : Promise.resolve({}),
        ]);

        const servicesList = await UserServiceService.GetUserServiceList(
          serviceOccurenceExternalId ? serviceOccData.service.externalId : null,
        );

        setServices(servicesList);
        setServiceOcc(serviceOccData);

        if (serviceOccData?.externalId) {
          setIsNewSO(false);
        }

        const criticalNotes = serviceOccData?.notes?.filter((e) => e.isCritical) ?? [];
        if (criticalNotes.length) {
          criticalNotes.forEach((e) => {
            addToast(e.note, e.externalId, "CUSTOM", true, e.color);
          });
        }
      } catch (err) {
        console.error(err);
        addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
      } finally {
        setLoading(false);

        // autorise la gestion du dirty seulement après le chargement complet
        setTimeout(() => {
          didInitRef.current = true;
        }, 0);
      }
    };

    fetchData();
  }, [serviceOccurenceExternalId, addToast]);

  useEffect(() => {
    applyModalUiPolicy();
  }, [activeTab, isCurrentTabDirty, isNewSO, serviceOcc]);

  return loading ? (
    <div className={styles.savingOverlay}>
      <LoadingSpinner />
    </div>
  ) : (
    <div className={styles.container}>
      <ServiceOccurenceForm
        key={serviceOccurenceExternalId ?? "new"}
        ref={formRef}
        client={serviceOcc.client ?? client}
        horse={serviceOcc.horse ?? horse ?? { externalId: -1 }}
        serviceOccurence={serviceOcc}
        services={services}
        isNew={isNewSO}
        activeTab={activeTab}
        onTabChange={(tabKey) => {
          setActiveTab(tabKey);
        }}
        onDirtyChange={(val) => {
          if (!didInitRef.current) return;
          setIsCurrentTabDirty(!!val);
        }}
      />
    </div>
  );
});

export default UpsertServiceOccurenceModal;
